/**
 * Sentry for speak, web and native (plan docs/speak-plan/49-ota-live-updates.md § 7):
 * the health signal of the OTA rollout (a bundle that crashes shows up as new issues of
 * its release) and the crash reporting the apps never had.
 *
 * DEFERRED LOAD (Club Arena pattern): `@sentry/react` stays out of the static graph of
 * the entry — it would delay every first paint. `initSentry()` (main.tsx, before the
 * first render) only installs early `error` / `unhandledrejection` listeners that QUEUE
 * what happens before the SDK; the SDK comes in through a dynamic `import()` once the
 * page has loaded and its LCP settled (`whenLcpSettled`), drains the queue and replaces
 * the early listeners with its own. The app's own breadcrumbs, messages and tags go
 * through the wrappers below, which queue too. What is lost from that window: the SDK's
 * automatic breadcrumbs (fetch/console/history exist only from its init).
 *
 * OFF without `VITE_SENTRY_DSN` (local dev, tests, any build that does not set it — the
 * native build-env files leave it empty until the project exists) and in the Vite dev
 * server: nothing is queued, nothing is loaded. When the DSN is set, vite.config.ts makes
 * the build fail unless `VITE_SENTRY_ENVIRONMENT` and `VITE_APP_VERSION` are set too.
 *
 * - Release `speak@<VITE_APP_VERSION>`: `<sha>-ota-<platform>` for an OTA bundle,
 *   `<sha>-apk|ipa` for a store build (native/scripts/with-build-env.mjs); the web build
 *   gets it from its own build args. It is what the OTA auto-rollout reads.
 * - Tags `ota_bundle` / `native_build` come from the OTA controller (lib/native/ota.ts).
 * - No session replay, no tracing (sample rate 0), no default PII. `beforeSend` /
 *   `beforeBreadcrumb` scrub credentials: the `Authorization` header and cookies, `?token=`
 *   in URLs (the native voice WS carries the JWT as `?token=`; the email links carry
 *   one-time tokens), `?code=` / `?otp=`, bearer tokens and JWTs in any string, OTP codes
 *   next to an `otp` / `code` label.
 *
 * Telemetry never affects the user: every call is wrapped and never throws.
 */
import type { Breadcrumb, Event as SentryEvent } from '@sentry/react';
import type * as SentrySdk from '@sentry/react';

type SentryModule = typeof SentrySdk;

/** Live SDK — null until the deferred import resolves. */
let sdk: SentryModule | null = null;
/** Sentry is off for this session (no DSN / dev server): nothing is queued. */
let disabled = false;
let initRequested = false;

const pending: Array<(s: SentryModule) => void> = [];
const PENDING_CAP = 50;

function run(op: (s: SentryModule) => void): void {
  if (sdk) {
    try {
      op(sdk);
    } catch {
      // Telemetry never affects the user.
    }
    return;
  }
  if (disabled) return;
  if (pending.length >= PENDING_CAP) pending.shift();
  pending.push(op);
}

/** The real second parameter of `Sentry.captureMessage` (a level or a CaptureContext). */
type CaptureMessageContext = Parameters<SentryModule['captureMessage']>[1];
type CaptureExceptionContext = Parameters<SentryModule['captureException']>[1];

/** Breadcrumb to Sentry — queued until the deferred SDK loads. */
export function sentryAddBreadcrumb(crumb: Breadcrumb): void {
  run((s) => s.addBreadcrumb(crumb));
}

/** captureMessage to Sentry — queued until the deferred SDK loads. */
export function sentryCaptureMessage(message: string, context?: CaptureMessageContext): void {
  run((s) => {
    s.captureMessage(message, context);
  });
}

/** captureException to Sentry — queued until the deferred SDK loads. */
export function sentryCaptureException(error: unknown, context?: CaptureExceptionContext): void {
  run((s) => {
    s.captureException(error, context);
  });
}

/** setTags to Sentry — queued until the deferred SDK loads. Global scope: the tags travel on every later event. */
export function sentrySetTags(tags: Record<string, string>): void {
  run((s) => {
    s.setTags(tags);
  });
}

/**
 * setUser to Sentry — queued until the deferred SDK loads (FIFO: a sign-out before
 * the init arrives as `null` in order). The internal UUID only, never the email: it
 * is what lets Sentry count affected users (the OTA auto rollout pauses a bundle at
 * 3 users, plan 49 § 7).
 */
export function sentrySetUser(user: { id: string } | null): void {
  run((s) => {
    s.setUser(user);
  });
}

// ---------------------------------------------------------------------------
// Scrubbing
// ---------------------------------------------------------------------------

/** `?token=` / `&code=` / `#otp=`… query or fragment values (or a bare query string). */
const SECRET_PARAM_RE =
  /((?:^|[?&#])(?:token|access_token|refresh_token|id_token|code|otp)=)[^&#\s"'<>]*/gi;
const BEARER_RE = /(\bBearer\s+)[A-Za-z0-9._~+/=-]+/gi;
const JWT_RE = /\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]+/g;
/** A one-time code right after an `otp` / `code` label ("otp: 123456", "code=123456"). */
const OTP_RE = /(\b(?:otp|code)\b["']?\s*[:=]\s*["']?)\d{4,8}\b/gi;
const REDACTED = '[REDACTED]';

/** Removes credentials from one string. Exported for tests. */
export function scrubString(value: string): string {
  return value
    .replace(SECRET_PARAM_RE, `$1${REDACTED}`)
    .replace(BEARER_RE, `$1${REDACTED}`)
    .replace(JWT_RE, REDACTED)
    .replace(OTP_RE, `$1${REDACTED}`);
}

const SECRET_HEADERS = new Set(['authorization', 'cookie', 'set-cookie', 'x-auth-token']);

function scrubRecord(record: Record<string, unknown> | undefined): void {
  if (!record) return;
  for (const [key, value] of Object.entries(record)) {
    if (SECRET_HEADERS.has(key.toLowerCase())) record[key] = REDACTED;
    else if (typeof value === 'string') record[key] = scrubString(value);
  }
}

/** Scrubs a breadcrumb in place and returns it. Exported for tests. */
export function scrubBreadcrumb(crumb: Breadcrumb): Breadcrumb {
  if (typeof crumb.message === 'string') crumb.message = scrubString(crumb.message);
  scrubRecord(crumb.data);
  return crumb;
}

/** Scrubs an event in place and returns it. Exported for tests. */
export function scrubEvent<E extends SentryEvent>(event: E): E {
  if (typeof event.message === 'string') event.message = scrubString(event.message);
  for (const ex of event.exception?.values ?? []) {
    if (typeof ex.value === 'string') ex.value = scrubString(ex.value);
  }
  const request = event.request;
  if (request) {
    if (typeof request.url === 'string') request.url = scrubString(request.url);
    if (typeof request.query_string === 'string')
      request.query_string = scrubString(request.query_string);
    if (request.headers) scrubRecord(request.headers);
    if (request.cookies) request.cookies = {};
  }
  for (const crumb of event.breadcrumbs ?? []) scrubBreadcrumb(crumb);
  scrubRecord(event.extra);
  return event;
}

// ---------------------------------------------------------------------------
// Load gate + init
// ---------------------------------------------------------------------------

/**
 * Load gate of the SDK: page `load` + LCP settled — telemetry yields the bandwidth to
 * the content. With the LCP API (Chromium, the Android WebView): 2 s without a new
 * candidate, or the first interaction (which freezes the LCP by spec), with a hard
 * ceiling 10 s after load (a background tab paints nothing and emits no candidate).
 * Without it (Safari / WKWebView): `requestIdleCallback` with a 3 s timeout after load,
 * or 1.5 s where that does not exist either. Exported for tests.
 */
export function whenLcpSettled(cb: () => void): void {
  let fired = false;
  const cleanups: Array<() => void> = [];
  const fire = (): void => {
    if (fired) return;
    fired = true;
    for (const c of cleanups) c();
    cb();
  };

  const arm = (): void => {
    const supportsLcp =
      typeof PerformanceObserver !== 'undefined' &&
      (PerformanceObserver.supportedEntryTypes ?? []).includes('largest-contentful-paint');

    if (!supportsLcp) {
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(() => fire(), { timeout: 3000 });
      } else {
        setTimeout(fire, 1500);
      }
      return;
    }

    // The debounce starts at once: with `buffered` the candidates already emitted
    // arrive in the first (async) callback and re-arm it; a page with no eligible LCP
    // candidate fires on its own after 2 s.
    let settleTimer = window.setTimeout(fire, 2000);
    cleanups.push(() => clearTimeout(settleTimer));
    try {
      const po = new PerformanceObserver(() => {
        clearTimeout(settleTimer);
        settleTimer = window.setTimeout(fire, 2000);
      });
      po.observe({ type: 'largest-contentful-paint', buffered: true });
      cleanups.push(() => po.disconnect());
    } catch {
      // observe() can throw on old engines despite the feature check: the initial
      // settle timer (2 s) is the fallback.
    }

    const onInteract = (): void => fire();
    window.addEventListener('pointerdown', onInteract, { once: true, passive: true });
    window.addEventListener('keydown', onInteract, { once: true });
    cleanups.push(() => {
      window.removeEventListener('pointerdown', onInteract);
      window.removeEventListener('keydown', onInteract);
    });

    const ceiling = window.setTimeout(fire, 10_000);
    cleanups.push(() => clearTimeout(ceiling));
  };

  if (document.readyState === 'complete') arm();
  else window.addEventListener('load', arm, { once: true });
}

// Early listeners: catch the errors of the boot→SDK window and queue them.
const onEarlyError = (e: ErrorEvent): void => {
  const err = e.error instanceof Error ? e.error : new Error(String(e.message));
  run((s) => {
    s.captureException(err);
  });
};
const onEarlyRejection = (e: PromiseRejectionEvent): void => {
  const err = e.reason instanceof Error ? e.reason : new Error(String(e.reason));
  run((s) => {
    s.captureException(err);
  });
};

/** Starts Sentry for this page (main.tsx, once, before the first render). Never throws. */
export function initSentry(): void {
  if (initRequested || disabled) return;
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
  if (!dsn || import.meta.env.DEV) {
    disabled = true;
    pending.length = 0;
    return;
  }
  // Both guaranteed by vite.config.ts whenever the DSN is set (the build fails otherwise).
  const environment = import.meta.env.VITE_SENTRY_ENVIRONMENT as string;
  const release = `speak@${import.meta.env.VITE_APP_VERSION as string}`;

  initRequested = true;
  window.addEventListener('error', onEarlyError);
  window.addEventListener('unhandledrejection', onEarlyRejection);
  whenLcpSettled(() => {
    void loadAndInit(dsn, environment, release);
  });
}

async function loadAndInit(dsn: string, environment: string, release: string): Promise<void> {
  try {
    const Sentry = await import('@sentry/react');
    Sentry.init({
      dsn,
      environment,
      release,
      // No tracing and no replay (plan § 7): errors only.
      tracesSampleRate: 0,
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      sendDefaultPii: false,
      ignoreErrors: ['ResizeObserver loop', 'Non-Error promise rejection captured'],
      beforeBreadcrumb: (crumb) => scrubBreadcrumb(crumb),
      beforeSend: (event) => scrubEvent(event),
    });

    // The SDK installed its global handlers: the early ones would duplicate reports.
    window.removeEventListener('error', onEarlyError);
    window.removeEventListener('unhandledrejection', onEarlyRejection);

    sdk = Sentry;
    while (pending.length) {
      const op = pending.shift()!;
      try {
        op(Sentry);
      } catch {
        // telemetry never throws
      }
    }
  } catch (error) {
    // The SDK chunk did not load (offline first launch, a stale chunk): this session
    // goes without Sentry, and says so in the console.
    disabled = true;
    pending.length = 0;
    console.error('[sentry] could not load the SDK — Sentry off for this session', error);
  }
}
